import React, { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import styled from 'styled-components';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import WarningIcon from '@mui/icons-material/Warning';
import queryString from 'query-string';
import { Clinic } from '../../common/model/dto/clinic';
import { ClinicLocationWithAnalytics } from '../../common/model/dto/clinic-location';
import { CreateClinicLocationRequest } from '../../common/model/dto/create-clinic-location-request';
import { Resource } from '../../common/model/type/resource.enum';
import { FilterModel } from '../../common/model/ui/filter.model';
import { OrderBy } from '../../common/types';
import { getAxiosErrorMessage } from '../../common/utils/helpers';
import { useAdminClinicListMutations } from '../../hooks/use-admin-clinic-mutations';
import useAuthorized from '../../hooks/use-authorized';
import { useListQueryParams } from '../../hooks/use-list-query-params';
import { AdminClinicLocationService } from '../../services/admin/admin-clinic-location.service';
import OvCompactButton from '../UI/atoms/OvCompactButton';
import OvErrorMessage from '../UI/atoms/OvErrorMessage';
import OvLoadingIndicator from '../UI/atoms/OvLoadingIndicator';
import OvCreateClinicLocationDialog from '../UI/molecules/OvCreateClinicLocationDialog';
import OvListStateMessage from '../UI/molecules/OvListStateMessage';
import OvPaginationBar from '../UI/molecules/OvPaginationBar';
import OvRecordDetailsSection from '../UI/molecules/OvRecordDetailsSection';
import OvClinicLocationList from '../UI/organisms/OvClinicLocationList';

export const ADMIN_CLINIC_LOCATION_LIST_LIMIT = 10;
const CLINIC_LOCATION_LIST_FILTERS: FilterModel[] = [];
const DEFAULT_CLINIC_LOCATION_LIST_ORDER: OrderBy = {
  order_by: 'name',
  order_dir: 'asc',
};

const FilterableClinicLocationList: FC<{ selectedClinic: Clinic }> = ({
  selectedClinic,
}) => {
  const clinicId = selectedClinic.id as string;
  const { t } = useTranslation();
  const navigate = useNavigate();
  const applicationOperations = useAuthorized(Resource.Application);
  const clinicLocationOperations = useAuthorized(Resource.ClinicLocation);
  const clinicLocationCreateAllowed =
    clinicLocationOperations.create || applicationOperations.supervise;
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const { offset, orderBy, setOffset } = useListQueryParams(
    CLINIC_LOCATION_LIST_FILTERS,
    DEFAULT_CLINIC_LOCATION_LIST_ORDER
  );

  const query = queryString.stringify({
    limit: ADMIN_CLINIC_LOCATION_LIST_LIMIT,
    clinic_id: clinicId,
    offset,
    order_by: orderBy?.order_by,
    order_dir: orderBy?.order_dir,
  });

  const {
    data: paginatedClinicLocations,
    isPending,
    isFetching,
    isError,
    refetch,
  } = useQuery({
    queryKey: ['admin-clinic-locations', query],
    queryFn: () => AdminClinicLocationService.loadClinicLocationsByQuery(query),
    placeholderData: keepPreviousData,
  });

  const clinicLocations = (paginatedClinicLocations?.docs ??
    []) as ClinicLocationWithAnalytics[];
  const total = paginatedClinicLocations?.total ?? 0;

  const { createClinicLocation } = useAdminClinicListMutations();

  const handleOnSave = (request: CreateClinicLocationRequest) => {
    setIsDialogOpen(false);
    createClinicLocation.mutate(
      { clinicId, request },
      {
        onSuccess: (clinicLocation) =>
          navigate(
            `/clinics/${clinicLocation.clinic.id}/clinic-info/${clinicLocation.id}`
          ),
      }
    );
  };

  const renderContent = () => {
    if (isError) {
      return (
        <OvListStateMessage
          isAlert
          icon={<WarningIcon />}
          title={t('clinicLocationDetails.states.errorTitle')}
          description={t('clinicLocationDetails.states.errorDescription')}
        >
          <OvCompactButton onClick={() => refetch()}>
            {t('common.actions.retry')}
          </OvCompactButton>
        </OvListStateMessage>
      );
    }

    if (isPending) {
      return null;
    }

    if (clinicLocations.length > 0) {
      return (
        <OvClinicLocationList
          clinicId={clinicId}
          clinicLocations={clinicLocations}
        />
      );
    }

    return (
      <OvListStateMessage
        icon={<LocalHospitalIcon />}
        title={t('clinicLocationDetails.states.emptyTitle')}
        description={
          clinicLocationCreateAllowed
            ? t('clinicLocationDetails.states.emptyDescription')
            : t('clinicLocationDetails.states.emptyReadOnlyDescription')
        }
      />
    );
  };

  return (
    <OvRecordDetailsSection
      title={t('clinicDetails.tabs.clinicSites')}
      action={
        clinicLocationCreateAllowed && (
          <OvCompactButton
            icon={<AddCircleIcon />}
            onClick={() => setIsDialogOpen(true)}
          >
            {t('clinicLocationDetails.actions.addNewClinicSite')}
          </OvCompactButton>
        )
      }
    >
      {createClinicLocation.error && (
        <OvErrorMessage
          message={getAxiosErrorMessage(createClinicLocation.error)}
        />
      )}

      {renderContent()}

      <Pagination>
        <OvPaginationBar
          total={total}
          limit={ADMIN_CLINIC_LOCATION_LIST_LIMIT}
          offset={offset}
          onChange={(_, page) =>
            setOffset((page - 1) * ADMIN_CLINIC_LOCATION_LIST_LIMIT)
          }
        />
      </Pagination>

      {(isFetching || createClinicLocation.isPending) && (
        <OvLoadingIndicator position="fixed" />
      )}

      <OvCreateClinicLocationDialog
        isOpen={isDialogOpen}
        onCancel={() => setIsDialogOpen(false)}
        onSave={handleOnSave}
        title={t('clinicLocationDialogs.createClinicSiteTitle')}
        selectedClinic={selectedClinic}
      />
    </OvRecordDetailsSection>
  );
};

export default FilterableClinicLocationList;

const Pagination = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 0.75rem;
`;
